/* eslint-disable react/jsx-props-no-spreading */
import React, { useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  colors,
  createFilterOptions,
  InputAdornment,
  Paper,
  SxProps,
  Tooltip,
  Typography,
  TextField,
  autocompleteClasses,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Search } from '@mui/icons-material';
import { useAuth0 } from '@auth0/auth0-react';

import { MerchantAccess } from '../../store/modules/merchant/types';
import { isValidUUIDv4 } from '../../util/helpers';
import { useMerchant } from '../../contexts/MerchantContext';
import { useSwitchMerchant } from '../../hooks/useSwitchMerchant';
import { useLazyGetMerchantsByUserQuery } from '../../store/modules/merchant/api';
import { getAccessTokenWithAudience } from '../../util/auth0Utils';
import Logger from '../../util/logger';
import { useIsSkipifyUser } from '../../hooks';
import { useDebounce } from '../../hooks/useDebounce';
import { popoverTooltipSx } from '../../components/core';

/** Matches muted search icon size so idle name can sit optically centered. */
const IDLE_ICON_SLOT_PX = 16;

/** Desktop Viewing field: grow with the name, never past this. */
const VIEWING_MIN_WIDTH_MD = 200;
const VIEWING_MAX_WIDTH_MD = 260;
/**
 * Idle horizontal chrome outside the name glyphs:
 * inputRoot pl/pr 8 + search icon 16 + adornment ml 4 + outline ~2.
 * Keep generous so weight-600 names aren’t clipped before max.
 */
const VIEWING_IDLE_CHROME_MD = 56;

const measureMerchantNameWidth = (name: string, fontFamily: string) => {
  if (typeof document === 'undefined' || !name) return 0;
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  if (!ctx) return 0;
  // Match idle Viewing input: 14px / 600
  ctx.font = `600 14px ${fontFamily}`;
  return Math.ceil(ctx.measureText(name).width);
};

const PaperComponent = ({ children }: React.HTMLAttributes<HTMLElement>) => {
  const hasChildren = React.Children.toArray(children).some((child) => child !== null);

  return (
    <Paper
      variant="outlined"
      sx={{
        border: hasChildren ? `1px solid ${colors.grey[400]}` : 'none',
        fontSize: '14px',
        maxHeight: '180px',
        overflow: 'auto',
        [`& .${autocompleteClasses.listbox}`]: {
          maxHeight: 'initial',
          backgroundColor: 'background.paper',
        },
      }}
    >
      {children}
    </Paper>
  );
};

type SearchInputProps = {
  merchantUserId: string;
  merchantAccess: MerchantAccess[];
  currentMerchant: MerchantAccess;
  switchMerchant: (merchantId: string) => void;
  disabled?: boolean;
  isSearchEnabled?: boolean;
  sx?: SxProps;
};

const SearchInput = ({
  merchantUserId,
  merchantAccess,
  currentMerchant,
  switchMerchant,
  disabled = false,
  isSearchEnabled = true,
  sx,
}: SearchInputProps) => {
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const [merchantResults, setMerchantResults] = useState<MerchantAccess[]>([]);
  const [noMerchantsFound, setNoMerchantsFound] = useState(false);
  // Idle: desktop left-align + tip; mobile centered. Focused: always left for typing.
  const [isFocused, setIsFocused] = useState(false);
  const { getAccessTokenSilently } = useAuth0();
  const [getMerchantsByUser, { isFetching: isSearchLoading }] = useLazyGetMerchantsByUserQuery();

  // We could just use currentMerchant as the value for the autoComplete, but having this as a
  // separate state allows us to instantly set the value using the search results or the merchant
  // access array. Otherwise, we would need to wait for the fetching of this new merchant to
  // update the name displayed.
  const [selectedMerchant, setSelectedMerchant] = useState<MerchantAccess>(currentMerchant);

  const idleCentered = !isFocused && !isMdUp;
  const inputTextAlign = isFocused || isMdUp ? 'left' : 'center';
  const fieldLabel = isFocused ? 'Search merchants' : 'Viewing';
  // Idle: full name below the field (InfoTooltip popover style). Hidden while searching.
  const idleNameTip = !isFocused && selectedMerchant.name ? selectedMerchant.name : '';

  // Inputs don't size to their value — measure glyphs and clamp to [min, max].
  // Focused uses the same width (not a forced jump to max) so idle/focus don't disagree.
  const desktopFieldWidth = useMemo(() => {
    if (!isMdUp) return VIEWING_MIN_WIDTH_MD;
    const fontFamily = theme.typography.fontFamily ?? 'sans-serif';
    const textWidth = measureMerchantNameWidth(selectedMerchant.name, fontFamily);
    return Math.min(
      VIEWING_MAX_WIDTH_MD,
      Math.max(VIEWING_MIN_WIDTH_MD, textWidth + VIEWING_IDLE_CHROME_MD),
    );
  }, [isMdUp, selectedMerchant.name, theme.typography.fontFamily]);

  const fieldWidth = isMdUp ? desktopFieldWidth : '100%';

  const debouncedSearchMerchants = useDebounce(async (searchTerm: string) => {
    if (!getAccessTokenSilently) return;

    setNoMerchantsFound(false);
    try {
      const token = await getAccessTokenWithAudience(getAccessTokenSilently);
      const results = await getMerchantsByUser({
        userId: merchantUserId,
        token,
        params: isValidUUIDv4(searchTerm)
          ? { merchantId: searchTerm, pageNumber: 1, pageSize: 250 }
          : { merchantName: searchTerm, pageNumber: 1, pageSize: 250 },
      }).unwrap();
      const newMerchantResults = results?.data ?? [];
      setMerchantResults(newMerchantResults);
      if (newMerchantResults.length === 0) {
        setNoMerchantsFound(true);
      }
    } catch (error) {
      Logger.error('Error searching merchants:', error);
      setNoMerchantsFound(true);
    }
  }, 300);

  const options = useMemo(() => {
    if (!isSearchEnabled) return merchantAccess ?? [];
    if (merchantResults.length === 0) return [];

    const alreadyInResults = merchantResults.some(
      (m) => m.merchantId === selectedMerchant.merchantId,
    );
    if (alreadyInResults) return merchantResults;

    // Adding this so isOptionEqualToValue works as expected.
    return [...merchantResults, selectedMerchant];
  }, [isSearchEnabled, merchantAccess, merchantResults, selectedMerchant]);

  const idleSearchAdornment = (
    <InputAdornment position="end" sx={{ m: 0, ml: 0.5 }}>
      <Search
        data-testid="merchant-selector-search-icon"
        sx={{
          fontSize: IDLE_ICON_SLOT_PX,
          color: 'text.disabled',
          opacity: 0.9,
        }}
      />
    </InputAdornment>
  );

  // Mobile idle only: same width as the search icon so text-align:center is optically centered.
  const idleBalanceAdornment = idleCentered ? (
    <InputAdornment position="start" sx={{ m: 0, mr: 0.5 }}>
      <Box sx={{ width: IDLE_ICON_SLOT_PX, height: IDLE_ICON_SLOT_PX }} aria-hidden />
    </InputAdornment>
  ) : null;

  return (
    <Tooltip
      title={idleNameTip}
      placement="bottom"
      arrow
      enterDelay={300}
      disableInteractive
      disableFocusListener
      componentsProps={{
        popper: {
          sx: popoverTooltipSx({
            maxWidth: 320,
            textAlign: 'center',
            fontSize: '13px',
            lineHeight: '18px',
            px: '14px',
            py: '10px',
          }),
        },
      }}
    >
      {/* Wrapper so Tooltip can attach listeners without cloning Autocomplete internals */}
      <Box
        data-testid="merchant-selector-tip-anchor"
        sx={{
          width: fieldWidth,
          maxWidth: { md: VIEWING_MAX_WIDTH_MD },
          display: 'block',
        }}
      >
        <Autocomplete
          aria-label="Merchant Selector"
          blurOnSelect
          disabled={disabled}
          loading={isSearchLoading}
          PaperComponent={PaperComponent}
          options={options}
          getOptionLabel={(option) => option.name}
          noOptionsText={
            noMerchantsFound || !isSearchEnabled
              ? 'No merchants found'
              : 'Start typing to search for merchants'
          }
          loadingText="Searching for merchants..."
          filterOptions={
            isSearchEnabled
              ? (opts) => opts
              : createFilterOptions({ stringify: (option) => option.name })
          }
          isOptionEqualToValue={(option, value) => option.merchantId === value.merchantId}
          value={selectedMerchant}
          renderOption={(props, option) => {
            // Hiding the current merchant if it's not in the search results.
            const shouldHideOption =
              isSearchEnabled &&
              option.merchantId === selectedMerchant.merchantId &&
              !merchantResults.some((m) => m.merchantId === option.merchantId);
            if (shouldHideOption) return null;

            return (
              <li {...props} key={option.merchantId}>
                <Typography sx={{ fontSize: '14px' }}>{option.name}</Typography>
              </li>
            );
          }}
          sx={{
            width: fieldWidth,
            minWidth: { xs: 0, md: VIEWING_MIN_WIDTH_MD },
            maxWidth: { md: VIEWING_MAX_WIDTH_MD },
            mt: 0.5,
            // Idle: kill MUI's adorned / hasClearIcon side padding (~14–39px) that shows as
            // empty green gutters in DevTools. Keep only the 16px start spacer to balance search.
            ...(!isFocused
              ? {
                  [`& .${autocompleteClasses.inputRoot}`]: {
                    paddingLeft: '8px',
                    paddingRight: '8px',
                  },
                  [`&.${autocompleteClasses.hasClearIcon} .${autocompleteClasses.inputRoot}`]: {
                    paddingLeft: '8px',
                    paddingRight: '8px',
                  },
                }
              : {}),
            [`& .${autocompleteClasses.input}`]: {
              fontSize: 14,
              // Idle: slightly heavier so the name reads as the hero vs the notch label
              fontWeight: isFocused ? 400 : 600,
              textAlign: inputTextAlign,
              textOverflow: 'ellipsis',
            },
            '& .MuiOutlinedInput-input': {
              fontWeight: isFocused ? 400 : 600,
              textAlign: inputTextAlign,
              textOverflow: 'ellipsis',
            },
            // Keep notch label size consistent idle/focused (11px + scale(1)).
            // Legend must use the same uppercase + letter-spacing as the visible label —
            // otherwise the notch is sized for mixed-case and the last letter joins the border.
            '& .MuiInputLabel-root': {
              fontSize: 11,
              fontWeight: 500,
              letterSpacing: '0.03em',
              textTransform: 'uppercase',
              color: 'text.secondary',
            },
            '& .MuiInputLabel-root.MuiInputLabel-shrink': {
              fontSize: 11,
              transform: 'translate(14px, -9px) scale(1)',
            },
            '& .MuiInputLabel-root.Mui-focused': {
              color: 'text.secondary',
            },
            '& .MuiOutlinedInput-notchedOutline legend': {
              maxWidth: '100%',
            },
            '& .MuiOutlinedInput-notchedOutline legend span': {
              paddingLeft: '6px',
              paddingRight: '6px',
              fontSize: 11,
              fontWeight: 500,
              letterSpacing: '0.03em',
              textTransform: 'uppercase',
            },
            ...sx,
          }}
          forcePopupIcon={false}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              label={fieldLabel}
              placeholder=""
              InputLabelProps={{
                ...params.InputLabelProps,
                shrink: true,
              }}
              InputProps={{
                ...params.InputProps,
                // Idle mobile: muted search + start spacer for centering.
                // Idle desktop: left-aligned name + search (no spacer). Focused: clear X.
                startAdornment: isFocused ? params.InputProps.startAdornment : idleBalanceAdornment,
                endAdornment: isFocused ? params.InputProps.endAdornment : idleSearchAdornment,
              }}
              inputProps={{
                ...params.inputProps,
                style: {
                  ...(typeof params.inputProps?.style === 'object' ? params.inputProps.style : {}),
                  textAlign: inputTextAlign,
                  fontWeight: isFocused ? 400 : 600,
                  textOverflow: 'ellipsis',
                },
                onFocus: (e) => {
                  setIsFocused(true);
                  params.inputProps?.onFocus?.(e as React.FocusEvent<HTMLInputElement>);
                },
                onBlur: (e) => {
                  setIsFocused(false);
                  setNoMerchantsFound(false);
                  params.inputProps?.onBlur?.(e as React.FocusEvent<HTMLInputElement>);
                },
              }}
              onChange={(e) => {
                const value = e.target.value as string;
                if (!isSearchEnabled) return;
                if (value.length < 3) {
                  setMerchantResults([]);
                } else {
                  debouncedSearchMerchants(value);
                }
              }}
            />
          )}
          onChange={async (_event, value) => {
            if (!value) {
              setMerchantResults([]);
            }
            if (value?.merchantId && value.merchantId !== currentMerchant.merchantId) {
              setSelectedMerchant(value);
              switchMerchant(value.merchantId);
            }
          }}
        />
      </Box>
    </Tooltip>
  );
};

const PartnerSelect = ({ sx }: { sx?: SxProps }) => {
  const switchMerchant = useSwitchMerchant();
  const {
    merchantId,
    merchantName,
    merchantUserId,
    isLoading: isLoadingMerchant,
    merchantAccess,
  } = useMerchant();
  const isSearchEnabled = useIsSkipifyUser();

  if (!merchantUserId || !merchantId) {
    return (
      <Box
        sx={{
          width: { xs: '100%', md: 'auto' },
          borderLeft: { md: '1px solid' },
          borderColor: { md: 'border.main' },
          pl: { md: 2 },
          ...((sx as object) || {}),
        }}
      >
        <TextField
          variant="outlined"
          label="Viewing"
          InputLabelProps={{ shrink: true }}
          sx={{
            width: { xs: '100%', md: VIEWING_MIN_WIDTH_MD },
            minWidth: { xs: 0, md: VIEWING_MIN_WIDTH_MD },
            maxWidth: { md: VIEWING_MAX_WIDTH_MD },
            mt: 0.5,
            '& input': { fontSize: 14, fontWeight: 600, textAlign: 'center' },
            '& .MuiInputLabel-root': {
              fontSize: 11,
              fontWeight: 500,
              letterSpacing: '0.03em',
              textTransform: 'uppercase',
              color: 'text.secondary',
            },
            '& .MuiInputLabel-root.MuiInputLabel-shrink': {
              fontSize: 11,
              transform: 'translate(14px, -9px) scale(1)',
            },
            '& .MuiOutlinedInput-notchedOutline legend': {
              maxWidth: '100%',
            },
            '& .MuiOutlinedInput-notchedOutline legend span': {
              paddingLeft: '6px',
              paddingRight: '6px',
              fontSize: 11,
              fontWeight: 500,
              letterSpacing: '0.03em',
              textTransform: 'uppercase',
            },
          }}
          disabled
          value="Loading merchants..."
          inputProps={{ 'aria-label': 'Merchant Selector Loader' }}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: { xs: '100%', md: 'auto' },
        borderLeft: { md: '1px solid' },
        borderColor: { md: 'border.main' },
        pl: { md: 2 },
        display: 'flex',
        justifyContent: { xs: 'center', md: 'flex-start' },
        ...((sx as object) || {}),
      }}
    >
      <SearchInput
        key={merchantName}
        merchantUserId={merchantUserId}
        merchantAccess={merchantAccess ?? []}
        currentMerchant={{ name: merchantName, merchantId }}
        switchMerchant={switchMerchant}
        disabled={isLoadingMerchant}
        isSearchEnabled={isSearchEnabled}
      />
    </Box>
  );
};

export default PartnerSelect;
